import { useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import FavoriteIcon from '@mui/icons-material/Favorite';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns'; // Import the 'format' function for date formatting
import { UserContext } from "../contexts/UserContext";
import {Divider,Input , useMediaQuery,Dialog, DialogTitle,DialogContent, TextField, Avatar,IconButton,Breadcrumbs , Rating, Box,Card, CardMedia,CardContent, Typography, Button, Grid, Paper, Link as MuiLink } from '@mui/material';
import { AiFillLike } from "react-icons/ai";
import { MdKeyboardArrowUp,  MdKeyboardArrowDown} from "react-icons/md";
import AuthModal from "../components/AuthModal";
import LockIcon from '@mui/icons-material/Lock';
import Skeleton from '@mui/material/Skeleton';
import Recipe from "../components/Recipe";
import SideContent from "../components/SideContent";


// Utility function to extract H1 tags from post content
const extractHeadings = (content) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;

  const headings = Array.from(tempDiv.querySelectorAll('h1'));

  headings.forEach((heading) => {
    const id = heading.textContent.replace(/\s+/g, '-').toLowerCase();
    heading.setAttribute('id', id); // Add ID to the heading element
  });

  // Return both the modified content and the heading data
  return {
    updatedContent: tempDiv.innerHTML,
    headingList: headings.map((heading) => ({
      text: heading.textContent,
      id: heading.textContent.replace(/\s+/g, '-').toLowerCase(), // Create a simple ID from the heading text
    })),
  };
};


export default function PostPage() {
const { id } = useParams();
const [postInfo, setPostInfo] = useState(null);
const { userInfo, profileLoading, profileError } = useContext(UserContext); // Get user info and loading state

const [isFavorite, setIsFavorite] = useState(false);
const [isLiked, setIsLiked] = useState(false);
const [likesCount, setLikesCount] = useState(0);
const [viewsCount, setViewsCount] = useState(0);
const [comments, setComments] = useState([]);
const [newComment, setNewComment] = useState('');
const [newRating, setNewRating] = useState(null);
const [replyContent, setReplyContent] = useState('');
const [replyLikesCount, setReplyLikesCount] = useState(0);
const [replyToCommentId, setReplyToCommentId] = useState(null);
const [showReplies, setShowReplies] = useState({}); // To toggle replies visibility
const [headings, setHeadings] = useState([]);
const [showAuthModal, setShowAuthModal] = useState(false);
const [loading, setLoading] = useState(false); // Set to false initially
const [error, setError] = useState(null);
const [averageRating, setAverageRating] = useState(0);
const [totalReviews, setTotalReviews] = useState(0);
const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

const isAuthor = (array) =>  array?.user?.username === postInfo?.author?.username;
// Format the date like "13 Jan 2024"
const formattedDate = (date) =>  date ? format(new Date(date), 'dd MMM yyyy') : '';

const formattedDateWithTime = (date) =>  date ? format(new Date(date), 'dd MMM yyyy h:mma') : '';

// Toggle favorite status (like/unlike post)
const toggleFavorite = async () => {
  // Check if user is authenticated by ensuring userInfo and a valid token exist
  if (profileError || !userInfo) {
    // console.log("User not authenticated, showing auth modal.");
    // console.log("auth modal statment:", showAuthModal);
    setShowAuthModal(true); // Show modal if user not logged in
    return; // Exit the function here to prevent further execution
  }



  // Log user info for debugging
  // console.log("User Info are:", userInfo);

  // If the user is authenticated, proceed with the fetch request
  try {
    const response = await fetch(`${process.env.REACT_APP_API_END_POINT}/blog/post/${id}/favorite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userInfo.token}` // Ensure token is sent with the request
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json(); // Log server error details
      console.error('Error:', errorData);
      throw new Error('Failed to toggle favorite');
    }

    const result = await response.json();

    // Toggle the favorite state and update the likes count
    setIsFavorite(!isFavorite);
    setLikesCount(isFavorite ? likesCount - 1 : likesCount + 1);
  } catch (error) {
    console.error('Error:', error);
  }
};




// Check if the post is already favorited by the user
useEffect(() => {
  if (userInfo && postInfo?.favoritedBy?.includes(userInfo?.id)) {
    setIsFavorite(true);
  }
  if (postInfo) {
    setLikesCount(postInfo?.favoritedBy?.length);
    setViewsCount(postInfo?.views || 0);
  }
}, [postInfo, userInfo]);




// Increment views count
useEffect(() => {
  const incrementViews = async () => {
    const response = await fetch(`${process.env.REACT_APP_API_END_POINT}/blog/post/${id}/view`, {
      method: 'POST',
      credentials: 'include',
    });
    if (response.ok) {
      const data = await response.json();
      setViewsCount(data.views); // Set the updated views count
 
    
   
    }

  };
  incrementViews();
}, [id]);


// Fetch post data
useEffect(() => {
  const fetchPostData = async () => {


    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_END_POINT}/blog/post/${id}`);
      const data = await response.json();
      setPostInfo(data);
      setLoading(false);
      setIsFavorite(data.isFavorited);

      const { updatedContent, headingList } = extractHeadings(data.content);
      setPostInfo(prevState => ({ ...prevState, content: updatedContent }));
      setHeadings(headingList);

      const comments = data.comments || [];
      const totalRating = comments.reduce((acc, comment) => acc + (comment.rating || 0), 0);
      const ratingCount = comments.filter(comment => comment.rating).length;
      const average = ratingCount > 0 ? totalRating / ratingCount : 0;
      setAverageRating(average);
      setTotalReviews(ratingCount);

    } catch (error) {
      console.error('Error fetching post data:', error);
      setError(error.message);
    } 
  };

  fetchPostData();
}, [id]); // Only depend on id

// Log loading changes
useEffect(() => {
  console.log('loading state changed:', loading);
}, [loading]);




const fetchComments = async () => {

  setError(null);   // Reset any existing errors

  try {
    const response = await fetch(`${process.env.REACT_APP_API_END_POINT}/blog/post/${id}/comments`); // Fetch only comments
    if (!response.ok) throw new Error('Failed to fetch comments');

    const data = await response.json();
    setComments(data); // Update comments state with fetched data
  } catch (error) {
    console.error('Error fetching comments:', error);
    setError(error.message); // Set error state for user feedback
  } finally {
 
  }
};


// Fetch comments when the component mounts
useEffect(() => {

  fetchComments();
}, [id]);


const handleCommentSubmit = async () => { 
  if (profileError || !userInfo) {
    // console.log("User not authenticated, showing auth modal.");
    // console.log("auth modal statment:", showAuthModal);
    setShowAuthModal(true); // Show modal if user not logged in
    return; // Exit the function here to prevent further execution
  }

  // Reset input fields if necessary
  const content = newComment.trim();
  if (!content) return; // Prevent submission of empty comments

  try {
    // Make API request to add a comment
    const response = await fetch(`${process.env.REACT_APP_API_END_POINT}/blog/post/${id}/comment`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userInfo.token}`
      },
      credentials: 'include',
      body: JSON.stringify({ content, rating: newRating })
    });

    if (!response.ok) throw new Error('Failed to add comment');

    // Fetch comments again after adding the new comment
    await fetchComments(); // Fetch updated comments from the backend

  } catch (error) {
    console.error('Error submitting comment:', error);
  } finally {
    setNewComment(''); // Clear input fields after submission
    setNewRating(null);
  }
};



// Handle reply submission
const handleReplySubmit = async (commentId, repliedUsername) => {
  if (profileError || !userInfo) {
    setShowAuthModal(true);
    return;
  }

  // Reset input fields if necessary
  const content = replyContent.trim();
  if (!content) return; // Prevent submission of empty replies

  const replyText = `@${repliedUsername} ${replyContent}`;

  try {
    const response = await fetch(`${process.env.REACT_APP_API_END_POINT}/blog/post/${id}/comment/${commentId}/reply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userInfo.token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ content: replyText }),
    });

    if (!response.ok) throw new Error('Failed to add reply');

    // Fetch updated comments after reply submission
    await fetchComments(); // Fetch updated comments from the backend

  } catch (error) {
    console.error('Error submitting reply:', error);
  } finally {
    setReplyContent(''); // Clear the reply input field
    setReplyToCommentId(null); // Reset the reply state
  }
};



// Handle liking a comment or reply

// Handle liking a comment or reply
const handleLike = async (commentId, replyId = null) => {
  if (profileError) {
    setShowAuthModal(true);
    return;
  }

  const endpoint = replyId
    ? `${process.env.REACT_APP_API_END_POINT}/blog/post/${id}/comment/${commentId}/reply/${replyId}/like`
    : `${process.env.REACT_APP_API_END_POINT}/blog/post/${id}/comment/${commentId}/like`;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userInfo.token}`,
      },
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Failed to toggle like');

    fetchComments();

  } catch (error) {
    console.error('Error toggling like:', error);
  }
};

// Check if the user has liked a specific comment
const userHasLikedComment = (comment) => {
  return Array.isArray(comment.likes) && comment.likes.includes(userInfo?.id);
};

// Check if the user has liked a specific reply
const userHasLikedReply = (reply) => {
  return Array.isArray(reply.likes) && reply.likes.includes(userInfo?.id);
};




// Toggle replies visibility
const toggleReplies = (commentId) => {
  setShowReplies((prev) => ({
    ...prev,
    [commentId]: !prev[commentId]
  }));
};


// if (loading) {
//   console.log('loading statment runned  : ', loading)
//   return (

//   <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
//        <Typography variant="h6">Loading...</Typography>
//      </Box>
//   )
// }

if (!postInfo) return '';

  // Show a loading indicator while fetching user infof
  if (profileLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6">One moment please...</Typography>
      </Box>
    );
  }


  return (

   <Grid 
   container

   columnGap={5}        
   sx={{
       display: 'flex',
       justifyContent: 'center',
       alignItems: 'start',
       padding: '3% 10%'
   }}
   >  
     

      <Grid 
      container
      sm={12} 
      md={7}
      lg={7}
       spacing={3} display="block" justifyContent="center"
      sx={{paddingY:{xs:'2rem', sm:'3rem'}}}
      >
         <Grid 
      item
      lg={12}
      md={12}
      sm={12}
      xs={12}
      sx={{marginY:{xs:'2rem', sm:'2rem'}}}
      >

      <Breadcrumbs aria-label="breadcrumb">
        <Link className="link" color="inherit" to="/blog">
          Blog
        </Link>
        {loading ? (
         <Skeleton animation="wave" height={30} width="100px" />

        ) : (
        <Typography sx={{ color: 'inherit', textTransform:'capitalize'}}>{postInfo.postType}</Typography>
        )}
        {loading ? (
         <Skeleton animation="wave" height={30} width="100px" />

        ) : (
          <Typography sx={{ color: 'text.primary', textTransform:'capitalize'  }}>{postInfo.title}</Typography>

        )}
      </Breadcrumbs>
      </Grid>

    <Box  sx={{ padding: { xs: 2, sm: 4 },  }}>
      {/* Post Title */}
      {loading ? (
          <>
          
          <Skeleton animation="wave" height={80} />
          <Skeleton animation="wave" height={80} width="80%" />
          </>
       
        ) : (

      <Typography variant="h4" component="h1" sx={{ mb: 2, textAlign: 'start', fontWeight: 'bold', fontSize: '3rem', textTransform:'capitalize' }} >
        {postInfo.title}
      </Typography>
        ) }

      {postInfo && (
        loading ?  
        <Skeleton animation="wave" height={30} width="60%" />
        : (
        <Box display="flex" alignItems="center" sx={{ marginBottom: '1rem' }}>
          <Rating value={averageRating} precision={0.5} readOnly />
          <Typography variant="body1" sx={{ marginLeft: '0.5rem' }}>
            {`${averageRating.toFixed(1)}/5 from ${totalReviews} reviews`}
          </Typography>
        </Box>
        ) 
      )}

      {loading ? (
            <>
              <Skeleton animation="wave" height={50} style={{ marginBottom: 2 }} />
              <Skeleton animation="wave" height={50} style={{ marginBottom: 2 }} />
              <Skeleton animation="wave" height={50} width="80%" />
            </>
      ): (
        <Typography variant="h6" color="textSecondary" sx={{mb: '3%'}}>{postInfo.summary}</Typography>
      )}

     

      {/* Edit Button for the Author */}
      {userInfo?.id === postInfo.author._id && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
               <Link className="edit-btn" to={`/blog/edit/${postInfo._id}`}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            Edit this post
          </Link>
        </Box>
      )}



   
    {/* "In this Post" Section (Table of Contents) */}
{headings.length > 0 && (

    loading ? (
      <>
      <Skeleton animation="wave" height={50} style={{ marginBottom: 6 }} />
      <Skeleton height={100} width="60%"  animation="wave" variant="rectangular" style={{ marginBottom: "2rem" }}  />
      </>
    ) : (

     <Box
        sx={{
          backgroundColor: '#eeeded', // Set background color
          mb: 4,
          p: 2,
          borderRadius: '8px', // Optional: Add border-radius for smoother edges
        }}
      >
    {/* Title for the Table of Contents */}
    <Box sx={{ mb: 2, fontSize: '1.5rem', fontWeight: 'bold' }}>
      In this post
    </Box>

    {/* List of Headings */}
    <ul
      style={{
        listStyle: 'none', // Remove bullets
        padding: 0,
        margin: 0,
      }}
    >
      {headings.map((heading, index) => (
        <li
          key={index}
          style={{
            marginBottom: '0.5rem', // Space between headings
          }}
        >
          <MuiLink
            onClick={() => {
              const element = document.getElementById(heading.id);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            underline="hover"
            sx={{
              color: '#3d54aa', // Link color
              fontWeight: 500,
              transition: 'color 0.3s',
              '&:hover': {
                color: '#ca7735', // Hover color
              },
              typography: { xs: 'body2', sm: 'body1', md: 'h6' }, // Responsive text size
              cursor: 'pointer',
            }}
          >
            {heading.text}
          </MuiLink>
        </li>
      ))}
    </ul>
  </Box>
    )
)}


           {/* Image */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        {loading ? (
        <Skeleton sx={{  height: '20vh',}} animation="wave" variant="rectangular" />
        )
        :(
          <img 
            src={postInfo.cover} 
            alt={postInfo.title} 
            style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
          />
        )
        
        }
      </Paper>
      {/* Post Content */}
        
        {loading ? (
       <Skeleton sx={{ height: '40vh',}} animation="wave" variant="rectangular" />
        ): (

    <Box sx={{ position: 'relative' }}>
     {(!userInfo?.role && postInfo.visability !== 'public') ? (
    <>
      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', // Semi-transparent black overlay
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
          borderRadius: '8px', // Optional: rounds the overlay edges
          zIndex: 1, // Ensure overlay is on top
        }}
      >
        <LockIcon sx={{ fontSize: '60px', mb: 2 }} /> {/* Lock Icon */}
        <Typography variant="h6">
          Sign up for a free account to access this content
        </Typography>
      </Box>
      <Box sx={{ visibility: 'hidden', height: '40vh' }}> {/* Hidden box to prevent interaction */}
        sign up to access this content
      </Box>
    </>
  ) : (
    <Box
      className="content"
      sx={{
        '& h1': {
          fontSize: '1.5rem',
          fontWeight: 'bold',
          mt: 3,
          mb: 2,
        },
        '& p': {
          fontSize: '1rem',
          lineHeight: '1.6',
          mb: 2,
        },
        '& img': {
          width: '100%',
          borderRadius: '8px',
          mb: 3,
        },
        '& ul': {
          listStyleType: 'square',
          listStylePosition: 'inside',
        },
        '& li::marker': {
          color: '#ca7735', // Set bullet color to your desired color
        },
        '& li': {
          display: 'list-item',
          color: 'inherit',
        },
      }}
      dangerouslySetInnerHTML={{ __html: postInfo.content }} // Add post content
    />
  )}
</Box>
        )}



         

              {postInfo.postType === 'recipe' && (
                loading ? (
                  <Skeleton sx={{height: '40vh',}} animation="wave" variant="rectangular" />
                ) : 
            (  <Recipe postData={postInfo}  />)
              )}



          {/* Post Meta Information */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        {/* Date and Tags */}
        <Box>
          {loading ? (

          <Skeleton animation="wave" height={50} width="100px" />

          ) : (

          <Typography variant="body2" color="textSecondary" gutterBottom>
            {formattedDate(postInfo.createdAt)}
          </Typography>
          )}
          <Typography variant="body2" color="textSecondary">
            {/* Render tags if you have any */}
          </Typography>
        </Box>

        {/* Likes and Views */}
        <Box display="flex" alignItems="center">
          {/* Favorite Button */}

            {loading ? (

            <Skeleton animation="wave" height={50} width="70px" sx={{marginRight: '1rem'}}/>
            ): (
              <>             
          <IconButton onClick={toggleFavorite} sx={{ mr:1, }} className={isFavorite ? 'post-icon-active' : 'post-icon' } >
            <FavoriteIcon />
          </IconButton>
          <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
            {likesCount} {likesCount === 1 ? 'Like' : 'Likes'}
          </Typography>
              </>
            )}

            {loading ? (
            <Skeleton animation="wave" height={50} width="70px" />

            ) : (
                <>
          {/* Views Button */}
          <IconButton disabled className="post-icon">
            <VisibilityIcon />
          </IconButton>
          <Typography variant="body2" color="textSecondary">
            {viewsCount} {viewsCount === 1 ? 'View' : 'Views'}
          </Typography>
                </>
            )}

        </Box>
      </Box>

      <Box sx={{display: 'flex', flexWrap:'wrap'}}>
        
        {loading ? (
       <Skeleton animation="wave" height={50} width="70px" sx={{marginRight: '1rem'}} />

        ) : (

          <Link to={'/blog'} className='post-category me-2 mb-2'   gutterBottom>
          {postInfo.postType}
          </Link>
        )}
        
        {loading ? (

        <Skeleton animation="wave" height={50} width="120px" />

        ) : (

          <Link to={'/blog'} className='post-category mb-2'   gutterBottom>
          {postInfo.category}
          </Link>
        )}
          
          </Box>

    </Box>
    
    {/* Comments Section */}
    <Box my={5} sx={{ padding: { xs: 2, sm: 4 },  }}>
    <Typography variant="h6" gutterBottom>{comments.length} {comments.length === 1 ? 'comment' : 'comments'}  </Typography>
    <Divider />


       {/* Display existing comments */}
       {comments.map((comment) => (
  <Box key={comment._id} sx={{ marginY: '5%' }}>
   <Box display="flex" alignItems="center" sx={{ marginBottom: '0.5rem' }}>
      <Avatar sx={{ marginRight: '1rem' }}>
        {comment?.user?.username ? comment.user.username.substring(0,2).toUpperCase() : ''}
      </Avatar>
      <Typography variant="body1" fontWeight="bold" sx={{backgroundColor: isAuthor(comment)? '#888888' : 'none', color: isAuthor(comment) ? 'white' : 'black', padding: '0.2rem 0.5rem', borderRadius: '15px'}}>@{comment?.user?.username}</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ marginLeft: 'auto' }}>
        {formattedDateWithTime(comment?.createdAt)}
      </Typography>
    </Box>
    <Rating  value={comment.rating} readOnly precision={0.5} />
    <Typography variant="body2" sx={{ marginTop: '0.5rem' }}>{comment?.content}</Typography>

    <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '1rem' }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
    {/* Like Button */}
    <IconButton onClick={() => handleLike(comment._id)} className={userHasLikedComment(comment) ? 'post-icon-active' : 'post-icon' }> 
      <AiFillLike /> 
    </IconButton>
    <Typography variant="body2" color="textSecondary">
      {comment?.likes?.length} {comment?.likes?.length === 1 ? 'like' : 'likes'}
    </Typography>
    </Box>
    {/* Reply Button */}
    <button className="btn-text" onClick={() => setReplyToCommentId(comment._id)}>
      Reply
    </button>
 
   </Box>
   <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {comment?.replies?.length > 0 && (
        <IconButton
          sx={{ display: 'flex', alignItems: 'center' }}
          className="btn-text"
          onClick={() => toggleReplies(comment._id)}
          color="textSecondary"
        >
          {showReplies[comment._id] ? (
            <MdKeyboardArrowUp className="me-1 arrow-icon" />
          ) : (
            <MdKeyboardArrowDown className="me-1 arrow-icon" />
          )}
          {comment?.replies?.length} Replies
        </IconButton>
      )}
    </Box>

    {/* Reply Form for this specific comment */}
    {replyToCommentId === comment._id && (
      <Box sx={{ marginTop: '1rem' }}>
        <Input
          fullWidth
          value={replyContent}
          onChange={(e) => setReplyContent(e.target.value)}
          placeholder={`Replying to @${comment?.user?.username}...`}
          required
        />
          
        <Box display="flex" alignItems="center" sx={{ marginTop: '1rem' }}>
          <Button onClick={() => setReplyToCommentId(null)} className="btn-text">
            Cancel
          </Button>
          <Button onClick={() => handleReplySubmit(comment._id, comment?.user?.username)} className="btn-text">
            Reply
          </Button>
        </Box>
      </Box>
    )}
    
    {/* Replies Section */}
    {showReplies[comment._id] && (
      <Box sx={{ marginLeft: '2rem' }}>
        {comment?.replies?.map((reply) => (
   
        <Box key={reply._id} sx={{ marginY: '5%' }}>
        <Box display="flex" alignItems="center" sx={{ marginBottom: '0.5rem' }}>
        <Avatar alt={reply?.user?.username} sx={{ marginRight: '1rem' }}>
        {reply?.user?.username ? reply?.user?.username.substring(0, 2).toUpperCase() : ''}
        </Avatar>
        <Typography variant="body1" fontWeight="bold" sx={{backgroundColor: isAuthor(reply)? '#888888' : 'none', color: isAuthor(reply) ? 'white' : 'black',}}>@{reply?.user?.username}</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ marginLeft: 'auto' }}>
                {formattedDateWithTime(reply.createdAt)}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ marginTop: '0.5rem' }}>{reply.content}</Typography>

             <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '1rem' }}>
             <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {/* Like Button for Reply */}
            <IconButton 
              onClick={() => handleLike(comment._id, reply._id)} 
              className={userHasLikedReply (reply) ? 'post-icon-active' : 'post-icon'}
            >
              <AiFillLike /> 
            </IconButton>

            <Typography variant="body2" color="textSecondary">
              {reply?.likes?.length} {reply?.likes?.length === 1 ? 'like' : 'likes'}
            </Typography>
            </Box>
            {/* Reply Button for nested replies */}
            <button className="btn-text" onClick={() => setReplyToCommentId(reply._id)}>
              Reply
            </button>
            </Box>
            {/* Reply Form for this specific reply */}
            {replyToCommentId === reply._id && (
              <Box sx={{ marginTop: '1rem' }}>
                <Input
                  fullWidth
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder={`Replying to @${reply.user.username}...`}
                  required
                />
               <Box display="flex" alignItems="center" sx={{ marginTop: '1rem' }}>
                <Button onClick={() => setReplyToCommentId(null)} className="btn-text">
                  Cancel
                </Button>
                <Button onClick={() => handleReplySubmit(comment._id, reply.user.username)} className="btn-text">
                  Reply
                </Button>
              </Box>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    )}
  

  </Box>

))}


      {/* Comment Submission Form */}
      <Box sx={{ marginTop: '2rem', }}>
      <Rating
          value={newRating}
          onChange={(event, newValue) => setNewRating(newValue)}
          precision={0.5}
       
          sx={{ my: 2, '& label': {display: 'block'}}}
        />
      <Input placeholder="Add a comment to this post..."
     fullWidth
     multiline
     rows={4}
     value={newComment}
     onChange={(e) => setNewComment(e.target.value)}
     required
    
    />

    <Box sx={{ display: 'flex', alignItems: 'center'}}>

       
       
       <Button className="unit-btn active" onClick={handleCommentSubmit} sx={{ marginTop: '1rem' }} >
          Send Comment
        </Button>
    </Box>

      </Box>

    </Box>

   
      </Grid>



       {/* Featured Section */}
        
         <Grid container md={4} lg={4}   
            sx={{marginY:isSmallScreen ? "10rem": ''}} 
            >
          <SideContent/>
            </Grid>
        

      <AuthModal showAuthModal={showAuthModal} setShowAuthModal={setShowAuthModal} />

   
   </Grid> 

    
  );
}
